import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CircleAlertIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TemplateDownloadSection } from "./TemplateDownloadSection";
import { CurrentFilesDownloadSection } from "./CurrentFilesDownloadSection";
import { FileUploadSection } from "./FileUploadSection";
import { useUploadBudget } from "./useUploadBudget";
import { customToast } from "@/utils/customToast";
import * as Sentry from "@sentry/react";
import { PostBudgetRequest, BudgetValidationErrorResponse, ResponseError } from "@norma-bi/bi-api";
import { useT, type Translator } from "@/i18n";

type BudgetDialogMode = "upload" | "edit";

type BudgetDialogProps = {
  show: boolean;
  onClose: () => void;
  mode: BudgetDialogMode;
  businessId: string;
  year: number;
};

const isBudgetValidationError = (data: unknown): data is BudgetValidationErrorResponse => {
  if (!(data && typeof data === "object" && "type" in data)) return false;

  switch (data.type) {
    case "EMPTY_CELL":
    case "INVALID_BRANCH_ID":
    case "INVALID_FILE":
    case "INVALID_FORMAT":
    case "INVALID_VALUE":
      return true;
    default:
      return false;
  }
};

const getErrorMessage = (
  error: BudgetValidationErrorResponse,
  { t, formatNumber }: Translator,
): string => {
  switch (error.type) {
    case "EMPTY_CELL":
      return t("budget.emptyCell", {
        row: formatNumber(error.context.row, { useGrouping: false }),
        col: error.context.col,
      });

    case "INVALID_BRANCH_ID":
      return t("budget.invalidBranchId", {
        branchId: error.context.branch_id,
        row: formatNumber(error.context.row, { useGrouping: false }),
        col: error.context.col,
      });

    case "INVALID_FILE":
      return t("budget.invalidFile");

    case "INVALID_FORMAT":
      return t("budget.invalidFormat", {
        row: formatNumber(error.context.row, { useGrouping: false }),
        col: error.context.col,
        expected: error.context.expected,
        got: error.context.got,
      });

    case "INVALID_VALUE":
      return t("budget.invalidValue", {
        row: formatNumber(error.context.row, { useGrouping: false }),
        col: error.context.col,
        expected: error.context.expected,
        got: error.context.got,
      });

    default:
      return t("budget.unexpectedError");
  }
};

export function BudgetDialog({ show, onClose, mode, businessId, year }: BudgetDialogProps) {
  const [isUploadReady, setIsUploadReady] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [currentFile, setCurrentFile] = React.useState<File | null>(null);

  const uploadBudget = useUploadBudget();
  const queryClient = useQueryClient();
  const translator = useT();
  const { t } = translator;

  const handleConfirm = React.useCallback(() => {
    if (mode === "edit") {
      setIsUploadReady(false);
    }
    onClose();
  }, [onClose, mode]);

  const handleFileChange = async (file: File | null) => {
    setError(null);
    setIsUploadReady(false);
    setCurrentFile(file);

    if (file) {
      if (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        setIsValidating(true);

        try {
          const budgetRequest: PostBudgetRequest = {
            business_id: businessId,
            year: year,
            body: file,
          };

          await uploadBudget.mutateAsync(budgetRequest);
          setIsUploadReady(true);
          queryClient.invalidateQueries({ queryKey: ["budget", businessId] });
          customToast(t("budget.uploadSucceeded"), "success", "bottom");
        } catch (error: unknown) {
          if (error instanceof ResponseError) {
            const responseData = await error.response.json();
            if (isBudgetValidationError(responseData)) {
              setError(getErrorMessage(responseData, translator));
              setCurrentFile(null);
            } else {
              setError(t("budget.unexpectedError"));
              setCurrentFile(null);
            }
          } else {
            setError(t("budget.unexpectedError"));
            setCurrentFile(null);
            if (mode === "upload") {
              Sentry.captureException(error, {
                tags: {
                  component: mode === "upload" ? "UploadBudgetDialog" : "EditBudgetDialog",
                  action: "handleFileChange",
                  error_type: "unexpected_error",
                },
                extra: {
                  fileName: file.name,
                  fileSize: file.size,
                },
              });
            }
          }
        } finally {
          setIsValidating(false);
        }
      } else {
        setError(t("budget.xlsxOnly"));
        setCurrentFile(null);
      }
    } else {
      setIsValidating(false);
      setIsUploadReady(false);
      setError(null);
    }
  };

  const dialogConfig = {
    upload: {
      title: t("budget.uploadTitle"),
      DownloadSection: TemplateDownloadSection,
      confirmText: t("common.done"),
      showConfirm: isUploadReady,
    },
    edit: {
      title: t("budget.editTitle"),
      DownloadSection: CurrentFilesDownloadSection,
      confirmText: t("common.close"),
      showConfirm: isUploadReady,
    },
  };

  const config = dialogConfig[mode];
  const DownloadSection = config.DownloadSection;

  // Once an edit is uploaded, only the confirm button closes the dialog.
  const onDismiss = mode === "edit" && isUploadReady ? undefined : onClose;

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onDismiss?.()}>
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={Boolean(onDismiss)}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="pe-6">{config.title}</DialogTitle>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-4 p-4">
          <DownloadSection />
          <FileUploadSection
            onFileSelect={handleFileChange}
            isValidating={isValidating}
            currentFile={currentFile}
          />
          {error && (
            <Alert variant="destructive" className="text-start">
              <CircleAlertIcon />
              <AlertTitle>{error}</AlertTitle>
              <AlertDescription>
                {t("budget.helpPrefix")}{" "}
                <a
                  href="https://www.youtube.com/watch?v=itYuGQBE6Oc"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("budget.helpLink")}
                </a>
                {t("budget.helpSuffix")}
              </AlertDescription>
            </Alert>
          )}
        </div>
        {config.showConfirm && (
          <DialogFooter>
            <Button onClick={handleConfirm}>{config.confirmText}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
